import _regeneratorRuntime from 'babel-runtime/regenerator';

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

function _asyncToGenerator(fn) {
  return function () {
    var gen = fn.apply(this, arguments);
    return new Promise(function (resolve, reject) {
      function step(key, arg) {
        try {
          var info = gen[key](arg);
          var value = info.value;
        } catch (error) {
          reject(error);
          return;
        }

        if (info.done) {
          resolve(value);
        } else {
          return Promise.resolve(value).then(function (value) {
            step("next", value);
          }, function (err) {
            step("throw", err);
          });
        }
      }

      return step("next");
    });
  };
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _possibleConstructorReturn(self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
}

import { Map as olMap, View, Geolocation } from 'ol';
import { unByKey } from 'ol/Observable';
import { defaults as controlDefaults, ScaleLine, Control } from 'ol/control';
import { Select } from 'ol/interaction';
import { click } from 'ol/events/condition';
import { Point } from 'ol/geom';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import React from 'react';
import PropTypes from 'prop-types';
import debounce from '../utils/debounce';
import { getBaatToken } from '../utils/baat';
import './Map.css';
import 'ol/ol.css';

var _React$createContext = React.createContext('map'),
    Provider = _React$createContext.Provider,
    Consumer = _React$createContext.Consumer;

var nextMapId = 0;

var Map = function (_React$Component) {
  _inherits(Map, _React$Component);

  function Map() {
    _classCallCheck(this, Map);

    var _this = _possibleConstructorReturn(this, _React$Component.call(this));

    _this.id = 'react-openlayers-map-' + ++nextMapId;
    _this.state = {
      isInited: false
    };
    _this.mapContext = {
      map: undefined,
      padding: [0, 0, 0, 0]
    };
    _this.mapContext.setCenterAndZoom = _this.setCenterAndZoom.bind(_this);
    _this.mapContext.setZoom = _this.setZoom.bind(_this);
    _this.mapContext.getPaddedCenter = _this.getPaddedCenter.bind(_this);
    _this.mapContext.getResolution = _this.getResolution.bind(_this);
    _this.mapContext.fitViewAndZoom = _this.fitViewAndZoom.bind(_this);
    _this.mapContext.forceUpdateMap = _this.forceUpdateMap.bind(_this);
    return _this;
  }

  Map.prototype.calcPaddedCenter = function calcPaddedCenter(target, zoom) {
    var size = this.map.getSize();
    var padding = this.mapContext.padding;
    var position = [(size[0] - padding[1] - padding[3]) / 2 + padding[3], (size[1] - padding[0] - padding[2]) / 2 + padding[0]];
    return this.calcCenterOn(target, zoom, position);
  };

  Map.prototype.getResolution = function getResolution(zoom) {
    var maxResolution = this.view.getMaxResolution();
    return maxResolution / Math.pow(2, zoom);
  };

  Map.prototype.calcCenterOn = function calcCenterOn(target, zoom, position) {
    var size = this.map.getSize(); // calculate rotated position

    var rotation = this.view.getRotation();
    var cosAngle = Math.cos(-rotation);
    var sinAngle = Math.sin(-rotation);
    var rotX = target[0] * cosAngle - target[1] * sinAngle;
    var rotY = target[1] * cosAngle + target[0] * sinAngle;
    var resolution = this.view.getResolution();

    if (zoom !== undefined && zoom !== this.view.getZoom()) {
      resolution = this.getResolution(zoom);
    }

    rotX += (size[0] / 2 - position[0]) * resolution;
    rotY += (position[1] - size[1] / 2) * resolution; // go back to original angle

    sinAngle = -sinAngle; // go back to original rotation

    var centerX = rotX * cosAngle - rotY * sinAngle;
    var centerY = rotY * cosAngle + rotX * sinAngle;
    return [centerX, centerY];
  };

  Map.prototype.getPaddedCenter = function getPaddedCenter() {
    var size = this.map.getSize();
    var padding = this.mapContext.padding; // Inverse padding

    var position = [(size[0] - padding[1] - padding[3]) / 2 + padding[1], (size[1] - padding[0] - padding[2]) / 2 + padding[2]];
    return this.calcCenterOn(this.view.getCenter(), this.view.getZoom(), position);
  };

  Map.prototype.setCenter = function setCenter(center) {
    this.view.animate({
      center: this.calcPaddedCenter(center),
      duration: 400
    });
  };

  Map.prototype.setCenterAndZoom = function setCenterAndZoom(center, zoom) {
    this.view.animate({
      zoom: zoom,
      center: this.calcPaddedCenter(center, zoom),
      duration: 400
    });
  };

  Map.prototype.setZoom = function setZoom(zoom) {
    this.view.animate({
      zoom: zoom,
      duration: 400
    });
  };

  Map.prototype.fitViewAndZoom = function fitViewAndZoom(geometry, maxZoom) {
    this.view.fit(geometry, {
      maxZoom: maxZoom || undefined,
      duration: 400,
      padding: this.mapContext.padding
    });
  };

  Map.prototype.forceUpdateMap = function forceUpdateMap() {
    this.map.updateSize();
    this.map.render();
  };

  Map.prototype.createMap = function createMap() {
    var _props = this.props,
        center = _props.center,
        enableRotation = _props.enableRotation,
        zoom = _props.zoom,
        projection = _props.projection,
        hideControls = _props.hideControls,
        minZoom = _props.minZoom,
        maxZoom = _props.maxZoom,
        hideScaleLine = _props.hideScaleLine,
        noInteraction = _props.noInteraction,
        zoomInTipLabel = _props.zoomInTipLabel,
        zoomOutTipLabel = _props.zoomOutTipLabel;
    this.registerProjs();
    this.view = new View({
      center: center,
      enableRotation: enableRotation || false,
      zoom: zoom,
      minZoom: minZoom,
      maxZoom: maxZoom,
      projection: projection
    });
    var button = document.createElement('button');
    button.className = 'glyphicon glyphicon-screenshot';
    this.map = new olMap({
      controls: hideControls ? [] : controlDefaults({
        attributionOptions: {
          collapsible: false
        },
        zoomOptions: {
          zoomInTipLabel: zoomInTipLabel,
          zoomOutTipLabel: zoomOutTipLabel
        }
      }).extend(hideScaleLine ? [] : [new ScaleLine()]),
      interactions: noInteraction ? [] : undefined,
      target: this.props.target || this.id,
      layers: [],
      view: this.view
    });
    this.mapContext.map = this.map;

    if (this.props.track) {
      this.addTrackControl();
    }

    this.onMapCreated();
  };

  Map.prototype.registerProjs = function registerProjs() {
    var projDefs = this.props.projDefs;
    projDefs && projDefs.forEach(function (proj) {
      proj4.defs(proj.epsg, proj.def);
    });
    register(proj4);
  };

  Map.prototype.addTrackControl = function addTrackControl() {
    var _this2 = this;

    var trackTipLabel = this.props.trackTipLabel;
    var button = document.createElement('button');
    button.title = trackTipLabel;
    button.innerHTML = '<svg class="icon-location" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 28"><path d="M21.891 5.453l-10 20c-0.172 0.344-0.516 0.547-0.891 0.547-0.078 0-0.156-0.016-0.234-0.031-0.453-0.109-0.766-0.5-0.766-0.969v-9h-9c-0.469 0-0.859-0.313-0.969-0.766s0.109-0.922 0.516-1.125l20-10c0.141-0.078 0.297-0.109 0.453-0.109 0.266 0 0.516 0.094 0.703 0.297 0.313 0.297 0.391 0.766 0.187 1.156z"></path></svg>'; // create a Geolocation object setup to track the position of the device

    var geolocation = new Geolocation({
      // Get the current map projection
      projection: this.map.getView().getProjection(),
      // Track the user position
      tracking: true
    });

    var handleLocation = function handleLocation() {
      // center the view on the position
      var p = geolocation.getPosition();

      if (p) {
        var zoom = 18;
        setTimeout(function () {
          _this2.mapContext.map.getView().setCenter([parseFloat(p[0]), parseFloat(p[1])]);

          _this2.mapContext.map.getView().setZoom(zoom);
        }, 250);
      } else {
        console.warn('No geolocation. Consider using https://');
      }
    };

    button.addEventListener('click', handleLocation, false);
    var element = document.createElement('div');
    element.className = 'react-openlayers--show-location ol-unselectable ol-control';
    element.appendChild(button);
    var LocationControl = new Control({
      element: element
    });
    this.map.addControl(LocationControl);
  };

  Map.prototype.onMapCreated = function onMapCreated() {
    var _this3 = this;

    this.addEvent(this.view.on('change:center', debounce(function (evt) {
      return _this3.onCenterChange();
    }, 500)));
    this.addEvent(this.view.on('change:resolution', debounce(function (evt) {
      return _this3.onZoomChange();
    }, 500)));
    var select = new Select({
      layers: function layers(layer) {
        return layer.get('selectable') !== false;
      },
      filter: function filter(feature) {
        return feature.get('selectable') !== false;
      },
      condition: click,
      style: false
    });
    this.map.addInteraction(select);
    select.on('select', function (e) {
      if (e.selected.length === 0) return;
      var cluster = e.selected[0];
      var features = cluster.get('features');

      if (features) {
        if (features.length === 1) {
          var feature = features[0];

          if (feature.onClick) {
            feature.onClick(_this3.dispatch);
          }
        } else if (features.length > 1) {
          _this3.setCenterAndZoom(cluster.getGeometry().getCoordinates(), Math.max(_this3.view.getZoom() + 1, 10));
        }
      } else if (cluster.onClick) {
        cluster.onClick(_this3.dispatch);
      }

      e.target.getFeatures().clear();
    });
    this.map.on('click', function (evt) {
      if (_this3.props.onMapClick) {
        _this3.props.onMapClick(evt);
      }
    });
    this.setState({
      isInited: true
    });
    this.onCenterChange();
    this.onZoomChange();

    if (this.props.extent) {
      this.fitViewAndZoom(this.props.extent);
    }
  };

  Map.prototype.componentDidMount = function () {
    var _ref = _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee() {
      var _this4 = this;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (!this.mapContext.map) {
                _context.next = 6;
                break;
              }

              this.map = this.mapContext.map;
              this.view = this.map.getView();
              this.onMapCreated();
              _context.next = 11;
              break;

            case 6:
              if (!this.props.baatUrl) {
                _context.next = 10;
                break;
              }

              _context.next = 9;
              return getBaatToken(this.props.baatUrl);

            case 9:
              this.mapContext.baatToken = _context.sent;

            case 10:
              // Delay to allow layout to "settle", avoiding mismatch between
              // OpenLayers viewport size and actual viewport size
              setTimeout(function () {
                _this4.createMap();
              }, 200);

            case 11:
            case 'end':
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function componentDidMount() {
      return _ref.apply(this, arguments);
    }

    return componentDidMount;
  }();

  Map.prototype.componentWillReceiveProps = function componentWillReceiveProps(nextProps) {
    var _props2 = this.props,
        zoom = _props2.zoom,
        center = _props2.center;

    if (zoom !== nextProps.zoom || center[0] !== nextProps.center[0] || center[1] !== nextProps.center[1]) {
      this.fitViewAndZoom(new Point(nextProps.center), nextProps.zoom);
    }
  };

  Map.prototype.onCenterChange = function onCenterChange() {
    if (this.props.onCenterChange) {
      this.props.onCenterChange(this.view.getCenter(), this.view.getZoom(), this.view.getProjection().getCode(), this.view.calculateExtent(this.map.getSize()));
    }
  };

  Map.prototype.onZoomChange = function onZoomChange() {
    if (this.props.onZoomChange) {
      this.props.onZoomChange(this.view.getZoom());
    }
  };

  Map.prototype.addEvent = function addEvent(key) {
    if (!this.eventKeys) this.eventKeys = [];
    this.eventKeys.push(key);
  };

  Map.prototype.removeEvent = function removeEvent(key) {
    unByKey(key);
    var idx = this.eventKeys.indexOf(key);
    if (idx !== -1) this.eventKeys.splice(idx, 1);
  };

  Map.prototype.componentWillUnmount = function componentWillUnmount() {
    // Disconnect from map
    this.eventKeys.forEach(function (key) {
      return unByKey(key);
    });
  };

  Map.prototype.render = function render() {
    return React.createElement('div', null, React.createElement('div', {
      id: this.id,
      className: 'react-openlayers--map'
    }), React.createElement('div', {
      className: 'react-openlayers--overlay'
    }, this.mapContext.map && React.createElement(Provider, {
      value: this.mapContext
    }, this.props.children)));
  };

  return Map;
}(React.Component);

var withMapContext = function withMapContext(WrappedComponent) {
  var WithMapContext = function (_React$Component2) {
    _inherits(WithMapContext, _React$Component2);

    function WithMapContext() {
      _classCallCheck(this, WithMapContext);

      return _possibleConstructorReturn(this, _React$Component2.apply(this, arguments));
    }

    WithMapContext.prototype.render = function render() {
      var _this6 = this;

      return React.createElement(Consumer, null, function (mapContext) {
        return React.createElement(WrappedComponent, _extends({
          mapContext: mapContext
        }, _this6.props));
      });
    };

    return WithMapContext;
  }(React.Component);

  return WithMapContext;
};

Map.defaultProps = {
  zoom: 18,
  center: [0, 0]
};
Map.propTypes = process.env.NODE_ENV !== "production" ? {
  target: PropTypes.string,
  onCenterChange: PropTypes.func,
  onZoomChange: PropTypes.func,
  onMapClick: PropTypes.func,
  center: PropTypes.array,
  projection: PropTypes.string,
  zoom: PropTypes.number,
  hideControls: PropTypes.bool,
  hideScaleLine: PropTypes.bool,
  track: PropTypes.bool,
  minZoom: PropTypes.number,
  maxZoom: PropTypes.number,
  children: PropTypes.node,
  projDefs: PropTypes.arrayOf(PropTypes.object),
  noInteraction: PropTypes.bool,
  baatUrl: PropTypes.string,
  extent: PropTypes.array,
  zoomInTipLabel: PropTypes.string,
  zoomOutTipLabel: PropTypes.string,
  trackTipLabel: PropTypes.string
} : {};
export { Consumer, withMapContext };
export default Map;